import React, { useEffect, useMemo, useState } from 'react'
import { lotteryService, type TenantRuleContentItem, type TenantRuleScope } from '../services/lotteryService'

interface RulesGuideModalProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  scope?: TenantRuleScope
  fallbackRules?: React.ReactNode
  fallbackGuides?: React.ReactNode
  dialogClassName?: string
  closeButtonLabel?: string
}

function replaceScopeVariables(value: string, scope?: TenantRuleScope): string {
  return String(value || '')
    .replace(/\{\{HUAY_NAME\}\}/g, String(scope?.huayName || '').trim())
    .replace(/\{\{HUAY_TYPE_NAME\}\}/g, String(scope?.huayTypeName || '').trim())
}

function getScopeLabel(scope?: TenantRuleScope): string {
  const parts = [scope?.huayName, scope?.huayTypeName].map((value) => String(value || '').trim()).filter(Boolean)
  return parts.join(' • ')
}

function renderRuleContent(item: TenantRuleContentItem, scope?: TenantRuleScope) {
  const content = replaceScopeVariables(item.content, scope)
  if (item.contentType === 'text' || item.contentType === 'markdown') {
    return <div className="fs-14" style={{ whiteSpace: 'pre-wrap' }}>{content}</div>
  }

  return <div className="fs-14" dangerouslySetInnerHTML={{ __html: content }} />
}

export const RulesGuideModal: React.FC<RulesGuideModalProps> = ({
  isOpen,
  onClose,
  title = 'กฏกติกาและข้อบังคับ',
  scope,
  fallbackRules,
  fallbackGuides,
  dialogClassName = 'modal-dialog modal-dialog-scrollable',
  closeButtonLabel = 'ปิด',
}) => {
  const [loading, setLoading] = useState(false)
  const [items, setItems] = useState<{ rules: TenantRuleContentItem[]; guides: TenantRuleContentItem[] }>({ rules: [], guides: [] })

  useEffect(() => {
    if (!isOpen) return

    let isMounted = true
    setLoading(true)
    void lotteryService.getTenantRulesAndGuides(scope).then((nextItems) => {
      if (isMounted) setItems(nextItems)
    }).finally(() => {
      if (isMounted) setLoading(false)
    })

    return () => {
      isMounted = false
    }
  }, [isOpen, scope?.huayId, scope?.huayName, scope?.huayTypeId, scope?.huayTypeName, scope?.tenantId])

  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') return

    const previousOverflow = document.body.style.overflow
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }

    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', handleKeyDown)

    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, onClose])

  const hasFetchedRules = items.rules.length > 0
  const hasFetchedGuides = items.guides.length > 0
  const showRulesSection = hasFetchedRules || Boolean(fallbackRules)
  const showGuidesSection = hasFetchedGuides || Boolean(fallbackGuides)
  const hasAnyContent = showRulesSection || showGuidesSection
  const scopeLabel = useMemo(() => getScopeLabel(scope), [scope])

  const renderedRules = useMemo(() => items.rules.map((item, index) => (
    <div key={item._id || item.code || index} className={index > 0 ? 'mt-4 pt-3 border-top' : ''}>
      {item.title ? (
        <p className="mb-2">
          <strong>{replaceScopeVariables(item.title, scope)}</strong>
        </p>
      ) : null}
      {renderRuleContent(item, scope)}
    </div>
  )), [items.rules, scope])

  const renderedGuides = useMemo(() => items.guides.map((item, index) => (
    <div key={item._id || item.code || index} className={index > 0 ? 'mt-4 pt-4 border-top' : ''}>
      {item.title ? (
        <p className="text-center mb-3">
          <strong><u>{replaceScopeVariables(item.title, scope)}</u></strong>
        </p>
      ) : null}
      {renderRuleContent(item, scope)}
    </div>
  )), [items.guides, scope])

  if (!isOpen) return null

  return (
    <div
      className="modal fade show rules-guide-modal"
      style={{ display: 'block', backgroundColor: 'rgba(23, 20, 18, 0.72)', overflowY: 'auto', padding: '16px 12px' }}
      tabIndex={-1}
      onClick={onClose}
    >
      <style>{`
        .rules-guide-modal {
          padding-top: 84px !important;
          padding-bottom: 16px !important;
        }
        .rules-guide-modal .rules-guide-scroll {
          scrollbar-width: thin;
          scrollbar-color: #d6b089 transparent;
        }
        .rules-guide-modal .rules-guide-scroll::-webkit-scrollbar {
          width: 8px;
        }
        .rules-guide-modal .rules-guide-scroll::-webkit-scrollbar-thumb {
          background: #d6b089;
          border-radius: 999px;
        }
        .rules-guide-modal .rules-guide-body table {
          width: 100%;
          min-width: 520px;
        }
        .rules-guide-modal .rules-guide-body img {
          max-width: 100%;
          height: auto;
          border-radius: 12px;
        }
        .rules-guide-modal .rules-guide-body p:last-child,
        .rules-guide-modal .rules-guide-body ul:last-child,
        .rules-guide-modal .rules-guide-body ol:last-child,
        .rules-guide-modal .rules-guide-body table:last-child {
          margin-bottom: 0;
        }
        @media (max-width: 767.98px) {
          .rules-guide-modal {
            padding-top: 58px !important;
          }
          .rules-guide-modal .rules-guide-dialog {
            min-height: auto !important;
          }
          .rules-guide-modal .rules-guide-header,
          .rules-guide-modal .rules-guide-footer {
            padding-left: 16px !important;
            padding-right: 16px !important;
          }
        }
      `}</style>
      <div
        className={`${dialogClassName} rules-guide-dialog`}
        style={{ maxWidth: 920, margin: '0 auto', minHeight: 'auto', display: 'flex', alignItems: 'flex-start' }}
        onClick={(event) => event.stopPropagation()}
      >
        <div
          className="modal-content border-0"
          style={{ width: '100%', borderRadius: 24, overflow: 'hidden', boxShadow: '0 22px 60px rgba(0, 0, 0, 0.28)' }}
        >
          <div className="modal-header rules-guide-header" style={{ position: 'sticky', top: 0, zIndex: 3, background: 'linear-gradient(135deg, #fff7ef 0%, #fff 100%)', borderBottom: '1px solid #f1e4d7', padding: '14px 20px' }}>
            <div>
              <h5 className="modal-title t-medium c-red mb-1">{title}</h5>
              <div className="small" style={{ color: '#8a6c52' }}>
                {scopeLabel || 'รวมกติกาและวิธีตรวจผลในหน้าจอเดียว'}
              </div>
            </div>
            <button
              type="button"
              className="btn-close rounded-circle"
              style={{ backgroundColor: '#fff', boxShadow: '0 4px 14px rgba(0,0,0,0.1)' }}
              onClick={onClose}
            >
              ×
            </button>
          </div>
          <div
            className="modal-body rules-guide-scroll"
            style={{ padding: 0, maxHeight: 'calc(100vh - 240px)', overflowY: 'auto', overscrollBehavior: 'contain', backgroundColor: '#fffaf5' }}
          >
            {loading ? (
              <div className="text-center py-5 c-dark">กำลังโหลดข้อมูล...</div>
            ) : hasAnyContent ? (
              <div className="p-3 p-md-4 rules-guide-body">
                {showRulesSection && (
                  <section
                    className="mb-4"
                    style={{ backgroundColor: '#fff', border: '1px solid #f2e2d2', borderRadius: 20, padding: '18px 18px 14px' }}
                  >
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                      <div>
                        <div className="small fw-bold text-uppercase" style={{ color: '#d46f20', letterSpacing: '0.08em' }}>
                          Rules
                        </div>
                        <div className="fw-bold c-dark">กฎและข้อกำหนด</div>
                      </div>
                      <div
                        className="small"
                        style={{ backgroundColor: '#fff2e7', color: '#b25b19', padding: '6px 10px', borderRadius: 999 }}
                      >
                        ควรอ่านก่อนยืนยันรายการ
                      </div>
                    </div>
                    <div className="c-dark" style={{ lineHeight: 1.7 }}>
                      {hasFetchedRules ? renderedRules : fallbackRules}
                    </div>
                  </section>
                )}
                {showGuidesSection && (
                  <section
                    style={{ backgroundColor: '#fff', border: '1px solid #f2e2d2', borderRadius: 20, padding: '18px 18px 14px' }}
                  >
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                      <div>
                        <div className="small fw-bold text-uppercase" style={{ color: '#8b5cf6', letterSpacing: '0.08em' }}>
                          Guide
                        </div>
                        <div className="fw-bold c-dark">วิธีการดูผลรางวัล</div>
                      </div>
                      <div
                        className="small"
                        style={{ backgroundColor: '#f4efff', color: '#6d4cc9', padding: '6px 10px', borderRadius: 999 }}
                      >
                        เลื่อนดูรายละเอียดเพิ่มเติมได้
                      </div>
                    </div>
                    <div className="c-dark" style={{ lineHeight: 1.7, overflowX: 'auto' }}>
                      {hasFetchedGuides ? renderedGuides : fallbackGuides}
                    </div>
                  </section>
                )}
              </div>
            ) : (
              <div className="text-center py-5 c-dark">ไม่พบข้อมูลกฎหรือวิธีการดูผลรางวัล</div>
            )}
          </div>
          <div className="modal-footer rules-guide-footer justify-content-center" style={{ position: 'sticky', bottom: 0, zIndex: 3, backgroundColor: '#fff', borderTop: '1px solid #f1e4d7', padding: '14px 20px' }}>
            <button
              type="button"
              className="btn btn-secondary"
              style={{ minWidth: 140, borderRadius: 999 }}
              onClick={onClose}
            >
              {closeButtonLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RulesGuideModal
